import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiCheckCircle,
    FiFileText,
    FiLoader,
    FiRotateCcw,
    FiX,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    confirmCreditNote,
    createCreditNote,
    creditNoteErrorSelector,
    creditNoteLoadingSelector,
} from "../../../../../../../store/billing/creditNotes/creditNoteSlice";

import "./CreateCreditNoteDialog.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const CREDIT_NOTE_REASONS = [
    {
        value:
            "SALES_RETURN",

        label:
            "Sales Return",
    },
    {
        value:
            "DAMAGED_ITEM",

        label:
            "Damaged Item",
    },
    {
        value:
            "WRONG_ITEM",

        label:
            "Wrong Item",
    },
    {
        value:
            "PRICE_ADJUSTMENT",

        label:
            "Price Adjustment",
    },
    {
        value:
            "OTHER",

        label:
            "Other",
    },
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(
                value
            ) +
            Number.EPSILON
        ) *
        100
    ) / 100;

const roundQuantity = (
    value
) =>
    Math.round(
        (
            toNumber(
                value
            ) +
            Number.EPSILON
        ) *
        1000
    ) / 1000;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(
            value
        )
    );

const formatDateInput = (
    value = new Date()
) => {
    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }

    const year =
        date.getFullYear();

    const month =
        String(
            date.getMonth() +
            1
        ).padStart(
            2,
            "0"
        );

    const day =
        String(
            date.getDate()
        ).padStart(
            2,
            "0"
        );

    return `${year}-${month}-${day}`;
};

const getInvoiceId = (
    invoice
) =>
    invoice?._id ||
    invoice?.id ||
    "";

const getInvoiceNumber = (
    invoice
) =>
    invoice
        ?.documentNumber ||
    invoice
        ?.invoiceNumber ||
    invoice
        ?.invoiceNo ||
    "-";

const getInvoiceCustomerName = (
    invoice
) =>
    invoice
        ?.customerSnapshot
        ?.customerName ||
    invoice
        ?.customerSnapshot
        ?.name ||
    invoice
        ?.customer
        ?.customerName ||
    invoice
        ?.customer
        ?.name ||
    invoice
        ?.customerName ||
    "Walk-in Customer";

const getItemId = (
    item
) =>
    item?._id ||
    item?.id ||
    "";

const getItemName = (
    item
) =>
    item?.itemName ||
    item?.productName ||
    item?.name ||
    item?.product
        ?.productName ||
    item?.product
        ?.name ||
    "Invoice Item";

const getItemQuantity = (
    item
) =>
    roundQuantity(
        item?.quantity ??
        item?.qty ??
        0
    );

const getItemRate = (
    item
) =>
    roundMoney(
        item?.rate ??
        item?.unitPrice ??
        item?.price ??
        0
    );

const getItemDiscount = (
    item
) =>
    roundMoney(
        item?.discountAmount ??
        item?.totalDiscount ??
        0
    );

const getItemTaxRate = (
    item
) => {
    const directRate =
        toNumber(
            item?.taxRate ??
            item?.gstRate ??
            item?.gstPercent ??
            0
        );

    if (
        directRate >
        0
    ) {
        return directRate;
    }

    const quantity =
        getItemQuantity(
            item
        );

    const rate =
        getItemRate(
            item
        );

    const discount =
        getItemDiscount(
            item
        );

    const taxableAmount =
        roundMoney(
            Math.max(
                quantity *
                rate -
                discount,
                0
            )
        );

    const taxAmount =
        roundMoney(
            item?.taxAmount ??
            item?.totalTax ??
            0
        );

    if (
        taxableAmount <=
        0 ||
        taxAmount <=
        0
    ) {
        return 0;
    }

    return roundMoney(
        taxAmount /
        taxableAmount *
        100
    );
};

const calculateReturnItem = (
    item,
    returnQuantity
) => {
    const originalQuantity =
        getItemQuantity(
            item
        );

    const quantity =
        roundQuantity(
            Math.min(
                Math.max(
                    toNumber(
                        returnQuantity
                    ),
                    0
                ),
                originalQuantity
            )
        );

    const rate =
        getItemRate(
            item
        );

    const originalDiscount =
        getItemDiscount(
            item
        );

    const discountPerUnit =
        originalQuantity >
            0
            ? roundMoney(
                originalDiscount /
                originalQuantity
            )
            : 0;

    const grossAmount =
        roundMoney(
            quantity *
            rate
        );

    const discountAmount =
        roundMoney(
            Math.min(
                quantity *
                discountPerUnit,
                grossAmount
            )
        );

    const taxableAmount =
        roundMoney(
            Math.max(
                grossAmount -
                discountAmount,
                0
            )
        );

    const taxRate =
        getItemTaxRate(
            item
        );

    const taxAmount =
        roundMoney(
            taxableAmount *
            taxRate /
            100
        );

    const totalAmount =
        roundMoney(
            taxableAmount +
            taxAmount
        );

    return {
        quantity,

        rate,

        grossAmount,

        discountAmount,

        taxableAmount,

        taxRate,

        taxAmount,

        totalAmount,
    };
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const CreateCreditNoteDialog = ({
    open,
    invoice,
    onClose,
    onCreated,
}) => {
    const dispatch =
        useDispatch();

    const loading =
        useSelector(
            creditNoteLoadingSelector
        ) || {};

    const apiError =
        useSelector(
            creditNoteErrorSelector
        ) || {};

    const invoiceId =
        getInvoiceId(
            invoice
        );

    const invoiceItems =
        useMemo(
            () =>
                Array.isArray(
                    invoice?.items
                )
                    ? invoice.items
                    : [],
            [
                invoice,
            ]
        );

    const [
        creditNoteDate,
        setCreditNoteDate,
    ] = useState(
        formatDateInput()
    );

    const [
        reason,
        setReason,
    ] = useState(
        "SALES_RETURN"
    );

    const [
        reasonDetails,
        setReasonDetails,
    ] = useState("");

    const [
        notes,
        setNotes,
    ] = useState("");

    const [
        selectedItems,
        setSelectedItems,
    ] = useState({});

    const [
        localError,
        setLocalError,
    ] = useState("");

    const submitting =
        Boolean(
            loading.create ||
            loading.confirm
        );

    /*
    |--------------------------------------------------------------------------
    | RESET
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            if (
                !open
            ) {
                return;
            }

            setCreditNoteDate(
                formatDateInput()
            );

            setReason(
                "SALES_RETURN"
            );

            setReasonDetails(
                ""
            );

            setNotes(
                ""
            );

            setSelectedItems(
                {}
            );

            setLocalError(
                ""
            );
        },
        [
            open,
            invoiceId,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | CALCULATED ITEMS
    |--------------------------------------------------------------------------
    */

    const calculatedItems =
        useMemo(
            () => {
                return invoiceItems
                    .map(
                        (
                            item
                        ) => {
                            const itemId =
                                getItemId(
                                    item
                                );

                            const selected =
                                selectedItems[
                                itemId
                                ];

                            if (
                                !selected
                                    ?.selected
                            ) {
                                return null;
                            }

                            const calculation =
                                calculateReturnItem(
                                    item,
                                    selected
                                        ?.returnQuantity
                                );

                            if (
                                calculation
                                    .quantity <=
                                0
                            ) {
                                return null;
                            }

                            return {
                                item,

                                itemId,

                                restoreStock:
                                    selected
                                        ?.restoreStock !==
                                    false,

                                ...calculation,
                            };
                        }
                    )
                    .filter(
                        Boolean
                    );
            },
            [
                invoiceItems,
                selectedItems,
            ]
        );

    const totals =
        useMemo(
            () => {
                const result =
                    calculatedItems.reduce(
                        (
                            total,
                            item
                        ) => {
                            total.grossAmount +=
                                item
                                    .grossAmount;

                            total.discountAmount +=
                                item
                                    .discountAmount;

                            total.taxableAmount +=
                                item
                                    .taxableAmount;

                            total.taxAmount +=
                                item
                                    .taxAmount;

                            total.creditAmount +=
                                item
                                    .totalAmount;

                            return total;
                        },
                        {
                            grossAmount:
                                0,

                            discountAmount:
                                0,

                            taxableAmount:
                                0,

                            taxAmount:
                                0,

                            creditAmount:
                                0,
                        }
                    );

                Object.keys(
                    result
                ).forEach(
                    (
                        key
                    ) => {
                        result[key] =
                            roundMoney(
                                result[
                                key
                                ]
                            );
                    }
                );

                return result;
            },
            [
                calculatedItems,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | ITEM HANDLERS
    |--------------------------------------------------------------------------
    */

    const handleItemSelection = (
        item
    ) => {
        const itemId =
            getItemId(
                item
            );

        if (
            !itemId
        ) {
            return;
        }

        setSelectedItems(
            (
                previous
            ) => {
                const existing =
                    previous[
                    itemId
                    ];

                const nextSelected =
                    !existing
                        ?.selected;

                return {
                    ...previous,

                    [itemId]: {
                        selected:
                            nextSelected,

                        returnQuantity:
                            nextSelected
                                ? existing
                                    ?.returnQuantity ||
                                getItemQuantity(
                                    item
                                )
                                : 0,

                        restoreStock:
                            existing
                                ?.restoreStock !==
                            false,
                    },
                };
            }
        );

        setLocalError(
            ""
        );
    };

    const handleQuantityChange = (
        item,
        value
    ) => {
        const itemId =
            getItemId(
                item
            );

        const maximumQuantity =
            getItemQuantity(
                item
            );

        const quantity =
            roundQuantity(
                Math.min(
                    Math.max(
                        toNumber(
                            value
                        ),
                        0
                    ),
                    maximumQuantity
                )
            );

        setSelectedItems(
            (
                previous
            ) => ({
                ...previous,

                [itemId]: {
                    ...previous[
                    itemId
                    ],

                    selected:
                        quantity >
                        0,

                    returnQuantity:
                        quantity,

                    restoreStock:
                        previous[
                            itemId
                        ]
                            ?.restoreStock !==
                        false,
                },
            })
        );

        setLocalError(
            ""
        );
    };

    const handleRestoreStockChange = (
        item
    ) => {
        const itemId =
            getItemId(
                item
            );

        setSelectedItems(
            (
                previous
            ) => ({
                ...previous,

                [itemId]: {
                    ...previous[
                    itemId
                    ],

                    selected:
                        true,

                    returnQuantity:
                        previous[
                            itemId
                        ]
                            ?.returnQuantity ||
                        getItemQuantity(
                            item
                        ),

                    restoreStock:
                        !(
                            previous[
                                itemId
                            ]
                                ?.restoreStock !==
                            false
                        ),
                },
            })
        );
    };

    const handleReset = () => {
        setSelectedItems(
            {}
        );

        setReasonDetails(
            ""
        );

        setNotes(
            ""
        );

        setLocalError(
            ""
        );
    };

    /*
    |--------------------------------------------------------------------------
    | SUBMIT
    |--------------------------------------------------------------------------
    */

    const validateForm = () => {
        if (
            !invoiceId
        ) {
            return "Sale invoice ID is missing.";
        }

        if (
            calculatedItems.length ===
            0
        ) {
            return "Please select at least one returned item.";
        }

        const invalidItem =
            calculatedItems.find(
                (
                    item
                ) =>
                    item.quantity <=
                    0 ||
                    item.quantity >
                    getItemQuantity(
                        item.item
                    )
            );

        if (
            invalidItem
        ) {
            return `Return quantity is invalid for ${getItemName(
                invalidItem.item
            )}.`;
        }

        if (
            totals.creditAmount <=
            0
        ) {
            return "Credit note amount must be greater than zero.";
        }

        if (
            reason ===
            "OTHER" &&
            !reasonDetails.trim()
        ) {
            return "Please enter reason details.";
        }

        return "";
    };

    const handleSubmit =
        async (
            shouldConfirm = false
        ) => {
            const validationError =
                validateForm();

            if (
                validationError
            ) {
                setLocalError(
                    validationError
                );

                return;
            }

            try {
                setLocalError(
                    ""
                );

                const payload = {
                    saleInvoiceId:
                        invoiceId,

                    creditNoteDate,

                    reason,

                    reasonDetails:
                        reasonDetails.trim(),

                    notes:
                        notes.trim(),

                    items:
                        calculatedItems.map(
                            (
                                item
                            ) => ({
                                saleInvoiceItemId:
                                    item.itemId,

                                returnQuantity:
                                    item.quantity,

                                restoreStock:
                                    item.restoreStock,
                            })
                        ),
                };

                const createdCreditNote =
                    await dispatch(
                        createCreditNote(
                            payload
                        )
                    ).unwrap();

                let finalCreditNote =
                    createdCreditNote;

                if (
                    shouldConfirm
                ) {
                    finalCreditNote =
                        await dispatch(
                            confirmCreditNote(
                                createdCreditNote
                                    ?._id
                            )
                        ).unwrap();
                }

                if (
                    typeof onCreated ===
                    "function"
                ) {
                    await onCreated(
                        finalCreditNote
                    );
                }

                onClose?.();
            } catch (
            error
            ) {
                setLocalError(
                    error?.message ||
                    error ||
                    "Unable to create credit note."
                );
            }
        };

    if (
        !open
    ) {
        return null;
    }

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div
            className="create-credit-note-overlay"
            role="presentation"
        >
            <div
                className="create-credit-note-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="create-credit-note-title"
            >
                <header className="create-credit-note-header">
                    <div className="create-credit-note-header-main">
                        <span className="create-credit-note-header-icon">
                            <FiRotateCcw />
                        </span>

                        <div>
                            <h2 id="create-credit-note-title">
                                Create Credit Note
                            </h2>

                            <p>
                                Return items against invoice{" "}
                                <strong>
                                    {getInvoiceNumber(
                                        invoice
                                    )}
                                </strong>
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="create-credit-note-close"
                        disabled={
                            submitting
                        }
                        onClick={
                            onClose
                        }
                        aria-label="Close"
                    >
                        <FiX />
                    </button>
                </header>

                <div className="create-credit-note-body">
                    {(localError ||
                        apiError.create ||
                        apiError.confirm) && (
                            <div className="create-credit-note-alert create-credit-note-alert-error">
                                <FiAlertCircle />

                                <span>
                                    {localError ||
                                        apiError.create ||
                                        apiError.confirm}
                                </span>
                            </div>
                        )}

                    <section className="create-credit-note-invoice-card">
                        <div>
                            <span>
                                Invoice
                            </span>

                            <strong>
                                {getInvoiceNumber(
                                    invoice
                                )}
                            </strong>
                        </div>

                        <div>
                            <span>
                                Customer
                            </span>

                            <strong>
                                {getInvoiceCustomerName(
                                    invoice
                                )}
                            </strong>
                        </div>

                        <div>
                            <span>
                                Credit Note Date
                            </span>

                            <input
                                type="date"
                                value={
                                    creditNoteDate
                                }
                                disabled={
                                    submitting
                                }
                                onChange={(
                                    event
                                ) =>
                                    setCreditNoteDate(
                                        event
                                            .target
                                            .value
                                    )
                                }
                            />
                        </div>
                    </section>

                    <section className="create-credit-note-section">
                        <div className="create-credit-note-section-heading">
                            <div>
                                <h3>
                                    <FiFileText />

                                    Returned Items
                                </h3>

                                <p>
                                    Select items and enter the quantity being returned.
                                </p>
                            </div>

                            <span>
                                {
                                    calculatedItems.length
                                }{" "}
                                selected
                            </span>
                        </div>

                        <div className="create-credit-note-table-wrapper">
                            <table className="create-credit-note-table">
                                <thead>
                                    <tr>
                                        <th className="create-credit-note-select-column">
                                            Select
                                        </th>

                                        <th>
                                            Item
                                        </th>

                                        <th className="create-credit-note-number-column">
                                            Sold Qty
                                        </th>

                                        <th className="create-credit-note-number-column">
                                            Return Qty
                                        </th>

                                        <th className="create-credit-note-number-column">
                                            Rate
                                        </th>

                                        <th className="create-credit-note-number-column">
                                            Tax
                                        </th>

                                        <th className="create-credit-note-number-column">
                                            Credit Amount
                                        </th>

                                        <th className="create-credit-note-stock-column">
                                            Restore Stock
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {invoiceItems.map(
                                        (
                                            item,
                                            index
                                        ) => {
                                            const itemId =
                                                getItemId(
                                                    item
                                                );

                                            const selected =
                                                selectedItems[
                                                itemId
                                                ];

                                            const calculation =
                                                calculateReturnItem(
                                                    item,
                                                    selected
                                                        ?.returnQuantity ||
                                                    0
                                                );

                                            return (
                                                <tr
                                                    key={
                                                        itemId ||
                                                        index
                                                    }
                                                    className={
                                                        selected
                                                            ?.selected
                                                            ? "create-credit-note-row-selected"
                                                            : ""
                                                    }
                                                >
                                                    <td className="create-credit-note-select-column">
                                                        <input
                                                            type="checkbox"
                                                            checked={
                                                                Boolean(
                                                                    selected
                                                                        ?.selected
                                                                )
                                                            }
                                                            disabled={
                                                                submitting
                                                            }
                                                            onChange={() =>
                                                                handleItemSelection(
                                                                    item
                                                                )
                                                            }
                                                        />
                                                    </td>

                                                    <td>
                                                        <strong>
                                                            {getItemName(
                                                                item
                                                            )}
                                                        </strong>

                                                        <span>
                                                            {item
                                                                ?.description ||
                                                                item
                                                                    ?.hsnSac ||
                                                                "Invoice item"}
                                                        </span>
                                                    </td>

                                                    <td className="create-credit-note-number-column">
                                                        {getItemQuantity(
                                                            item
                                                        )}
                                                    </td>

                                                    <td className="create-credit-note-number-column">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max={getItemQuantity(
                                                                item
                                                            )}
                                                            step="0.001"
                                                            disabled={
                                                                submitting ||
                                                                !selected
                                                                    ?.selected
                                                            }
                                                            value={
                                                                selected
                                                                    ?.selected
                                                                    ? selected
                                                                        ?.returnQuantity ??
                                                                    ""
                                                                    : ""
                                                            }
                                                            onChange={(
                                                                event
                                                            ) =>
                                                                handleQuantityChange(
                                                                    item,
                                                                    event
                                                                        .target
                                                                        .value
                                                                )
                                                            }
                                                        />
                                                    </td>

                                                    <td className="create-credit-note-number-column">
                                                        {formatCurrency(
                                                            getItemRate(
                                                                item
                                                            )
                                                        )}
                                                    </td>

                                                    <td className="create-credit-note-number-column">
                                                        {formatCurrency(
                                                            calculation
                                                                .taxAmount
                                                        )}
                                                    </td>

                                                    <td className="create-credit-note-number-column create-credit-note-credit-value">
                                                        {formatCurrency(
                                                            calculation
                                                                .totalAmount
                                                        )}
                                                    </td>

                                                    <td className="create-credit-note-stock-column">
                                                        <label className="create-credit-note-stock-toggle">
                                                            <input
                                                                type="checkbox"
                                                                checked={
                                                                    selected
                                                                        ?.selected
                                                                        ? selected
                                                                            ?.restoreStock !==
                                                                        false
                                                                        : false
                                                                }
                                                                disabled={
                                                                    submitting ||
                                                                    !selected
                                                                        ?.selected
                                                                }
                                                                onChange={() =>
                                                                    handleRestoreStockChange(
                                                                        item
                                                                    )
                                                                }
                                                            />

                                                            <span>
                                                                Yes
                                                            </span>
                                                        </label>
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    )}

                                    {!invoiceItems.length && (
                                        <tr>
                                            <td
                                                colSpan="8"
                                                className="create-credit-note-empty"
                                            >
                                                No invoice items are available.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <div className="create-credit-note-bottom-grid">
                        <section className="create-credit-note-section">
                            <h3>
                                Credit Note Details
                            </h3>

                            <div className="create-credit-note-form-grid">
                                <label>
                                    <span>
                                        Reason
                                    </span>

                                    <select
                                        value={
                                            reason
                                        }
                                        disabled={
                                            submitting
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setReason(
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    >
                                        {CREDIT_NOTE_REASONS.map(
                                            (
                                                option
                                            ) => (
                                                <option
                                                    key={
                                                        option.value
                                                    }
                                                    value={
                                                        option.value
                                                    }
                                                >
                                                    {
                                                        option.label
                                                    }
                                                </option>
                                            )
                                        )}
                                    </select>
                                </label>

                                <label>
                                    <span>
                                        Reason Details
                                    </span>

                                    <input
                                        type="text"
                                        maxLength="500"
                                        value={
                                            reasonDetails
                                        }
                                        disabled={
                                            submitting
                                        }
                                        placeholder="Optional details"
                                        onChange={(
                                            event
                                        ) =>
                                            setReasonDetails(
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </label>

                                <label className="create-credit-note-notes-field">
                                    <span>
                                        Notes
                                    </span>

                                    <textarea
                                        rows="4"
                                        maxLength="1000"
                                        value={
                                            notes
                                        }
                                        disabled={
                                            submitting
                                        }
                                        placeholder="Optional customer-visible notes"
                                        onChange={(
                                            event
                                        ) =>
                                            setNotes(
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </label>
                            </div>
                        </section>

                        <section className="create-credit-note-summary">
                            <h3>
                                Credit Summary
                            </h3>

                            <div>
                                <span>
                                    Gross Amount
                                </span>

                                <strong>
                                    {formatCurrency(
                                        totals
                                            .grossAmount
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Discount
                                </span>

                                <strong>
                                    -
                                    {formatCurrency(
                                        totals
                                            .discountAmount
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Taxable Amount
                                </span>

                                <strong>
                                    {formatCurrency(
                                        totals
                                            .taxableAmount
                                    )}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    Tax
                                </span>

                                <strong>
                                    {formatCurrency(
                                        totals
                                            .taxAmount
                                    )}
                                </strong>
                            </div>

                            <div className="create-credit-note-summary-total">
                                <span>
                                    Credit Amount
                                </span>

                                <strong>
                                    {formatCurrency(
                                        totals
                                            .creditAmount
                                    )}
                                </strong>
                            </div>
                        </section>
                    </div>
                </div>

                <footer className="create-credit-note-footer">
                    <button
                        type="button"
                        className="create-credit-note-button create-credit-note-reset-button"
                        disabled={
                            submitting
                        }
                        onClick={
                            handleReset
                        }
                    >
                        <FiRotateCcw />

                        Reset
                    </button>

                    <div className="create-credit-note-footer-actions">
                        <button
                            type="button"
                            className="create-credit-note-button create-credit-note-cancel-button"
                            disabled={
                                submitting
                            }
                            onClick={
                                onClose
                            }
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="create-credit-note-button create-credit-note-draft-button"
                            disabled={
                                submitting ||
                                calculatedItems.length ===
                                0
                            }
                            onClick={() =>
                                handleSubmit(
                                    false
                                )
                            }
                        >
                            {loading.create ? (
                                <>
                                    <FiLoader className="create-credit-note-spinner" />

                                    Saving...
                                </>
                            ) : (
                                <>
                                    <FiFileText />

                                    Save Draft
                                </>
                            )}
                        </button>

                        <button
                            type="button"
                            className="create-credit-note-button create-credit-note-confirm-button"
                            disabled={
                                submitting ||
                                calculatedItems.length ===
                                0
                            }
                            onClick={() =>
                                handleSubmit(
                                    true
                                )
                            }
                        >
                            {submitting ? (
                                <>
                                    <FiLoader className="create-credit-note-spinner" />

                                    Processing...
                                </>
                            ) : (
                                <>
                                    <FiCheckCircle />

                                    Create & Confirm
                                </>
                            )}
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default CreateCreditNoteDialog;